import { useCustom, useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Drawer,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import { enrollInPathwayInputSchema } from '@cc/schema';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateFnsDatePicker } from '../../components/Pickers';
import { SubmitFooter } from '../../components/SubmitFooter';
import { usePickerLocale } from '../homework/usePickerLocale';
import {
  PATHWAY_DETAIL_FIELDS,
  PATHWAY_LIBRARY_FIELDS,
  landingsOf,
  needsPrescribe,
  pickerOrder,
  type PathwayLibrary,
  type StepLanding,
} from './model';
import { usePathwayCommand } from './usePathwayCommand';
import type { PathwayDetail } from '../../schema/be-graphql-generated';

/**
 * E34 · EnrollInPathway — "Client Hub — Homework — Enroll in a pathway (drawer)" (cc-homework, chapter
 * "Pathways — Define & enroll (portal, E34)", screen e34e0004-0001-4000-8000-000000000016): enrol the
 * client in a pathway — pick a template (the practice's active ones, latest version), a start instant
 * (default now). The release gear takes it from there; manual steps wait for the therapist.
 *
 * Behavior lines implemented here: gesture Enroll → EnrollInPathway, i18n pathways.enroll.*; the preview
 * lists the steps and when each will land; format the picker lists a pathway and its copies side by side
 * ("copy of …") — the therapist chooses which client gets which copy, no random assignment (Q-e34-19 A),
 * i18n pathways.enroll.copies; error-display a paid CopingCard course, or a copy of one, is disabled without
 * the practice's prescribe courses capability ("Your plan does not include courses" → Billing — the client is
 * never asked to pay for what the therapist prescribes, Q-e34-22 A), i18n pathways.enroll.planRequired. A
 * client whose app cannot show homework yet is said so (Q-b1d-1′ A); a coded refusal is shown verbatim.
 */
type Props = {
  open: boolean;
  subject: string;
  clientName: string;
  appReady?: boolean;
  /** Known when the drawer opens (Q-mt-2 A): the client is not connected, or has switched sharing off. */
  notConnected?: boolean;
  onClose: () => void;
  onEnrolled: (enrollmentId: string) => void;
};

export const EnrollPathwayDrawer = ({ open, subject, clientName, appReady, notConnected, onClose, onEnrolled }: Props) => {
  const t = useTranslate();
  const pickerLocale = usePickerLocale();
  const { message } = AntdApp.useApp();
  const { run } = usePathwayCommand();
  const [pathwayId, setPathwayId] = useState<string | undefined>();
  const [startAt, setStartAt] = useState<Date | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPathwayId(undefined);
    setStartAt(new Date());
    setProblem(null);
  }, [open]);

  const { data: libraryData } = useCustom<PathwayLibrary>({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: open },
    meta: {
      operation: 'pathways',
      fields: PATHWAY_LIBRARY_FIELDS,
      variables: { includeRetired: { name: 'includeRetired', type: 'Boolean', value: false } },
    },
  });
  const capabilities = libraryData?.data?.capabilities ?? [];
  const mayPrescribe = capabilities.includes('prescribeCourses');
  const options = useMemo(() => pickerOrder(libraryData?.data?.pathways ?? []), [libraryData]);

  const { data: detailData } = useCustom<PathwayDetail>({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: open && Boolean(pathwayId) },
    meta: {
      operation: 'pathway',
      fields: PATHWAY_DETAIL_FIELDS,
      variables: { pathwayId: { name: 'pathwayId', type: 'ID!', value: pathwayId } },
    },
  });
  const detail = pathwayId && detailData?.data?.pathwayId === pathwayId ? detailData.data : null;
  const landings = detail ? landingsOf(detail.sections) : [];

  const landingText = (l: StepLanding) => {
    const due = l.dueDays === null ? '' : ` · ${t('pathways.enroll.dueIn', { count: l.dueDays })}`;
    switch (l.when) {
      case 'atStart':
        return `${t('pathways.enroll.atStart')}${due}`;
      case 'onDay':
        return `${t('pathways.enroll.onDay', { day: l.day })}${due}`;
      case 'afterPrevious':
        return `${
          l.delayDays
            ? t('pathways.enroll.afterPreviousDelay', { title: l.previousTitle, count: l.delayDays })
            : t('pathways.enroll.afterPrevious', { title: l.previousTitle })
        }${due}`;
      default:
        return `${t('pathways.enroll.manual')}${due}`;
    }
  };

  // Q-mt-2 A: a submit error clears on the next edit
  useEffect(() => {
    setProblem(null);
  }, [pathwayId, startAt]);

  const submit = async () => {
    setProblem(null);
    if (!pathwayId) {
      setProblem(t('pathways.enroll.required'));
      return;
    }
    const input = { pathwayId, subject, startAt: (startAt ?? new Date()).toISOString() };
    try {
      await enrollInPathwayInputSchema.validate(input);
    } catch {
      setProblem(t('pathways.enroll.failed'));
      return;
    }
    setSaving(true);
    const r = await run('enrollInPathway', input, t('pathways.enroll.failed'));
    setSaving(false);
    if (!r.ok) {
      setProblem(r.message);
      return;
    }
    message.success(t('pathways.enroll.done', { name: clientName }));
    onEnrolled(String(r.data.enrollmentId));
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={600}
      destroyOnClose
      title={t('pathways.enroll.title')}
      footer={
        <SubmitFooter
          errors={problem ? <Alert type="error" showIcon message={problem} data-testid="pathway-enroll-problem" /> : null}
        >
          <Button onClick={onClose} data-testid="pathway-enroll-cancel">
            {t('pathways.enroll.cancel')}
          </Button>
          <Button
            type="primary"
            loading={saving}
            disabled={!!notConnected}
            onClick={submit}
            data-testid="pathway-enroll-submit"
          >
            {t('pathways.enroll.submit')}
          </Button>
        </SubmitFooter>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }} data-testid="pathway-enroll-drawer">
        <Typography.Text type="secondary">{t('pathways.enroll.for', { name: clientName })}</Typography.Text>
        {notConnected ? (
          <Alert
            type="error"
            showIcon
            message={t('pathways.enroll.notConnected', { name: clientName })}
            data-testid="pathway-enroll-not-connected"
          />
        ) : null}
        {appReady === false ? (
          <Alert
            type="info"
            showIcon
            message={t('homework.assign.appNotReady', { name: clientName })}
            data-testid="pathway-enroll-app-not-ready"
          />
        ) : null}
        <div>
          <Typography.Text>{t('pathways.enroll.pathway')}</Typography.Text>
          <Select
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            value={pathwayId}
            placeholder={t('pathways.enroll.pathwayPlaceholder')}
            onChange={(v: string) => setPathwayId(v)}
            notFoundContent={t('pathways.enroll.none')}
            options={options.map((r) => {
              const locked = needsPrescribe(r) && !mayPrescribe;
              const copy = r.copiedFrom
                ? ` · ${t('pathways.enroll.copies', { name: r.copiedFromName ?? r.copiedFrom })}`
                : '';
              return {
                value: r.pathwayId,
                disabled: locked,
                label: `${r.depth ? '↳ ' : ''}${r.name} · ${t('pathways.library.version', { version: r.version })}${copy}${
                  locked ? ` — ${t('pathways.enroll.planRequired')}` : ''
                }`,
              };
            })}
            data-testid="pathway-enroll-pathway"
          />
          {options.some((r) => needsPrescribe(r)) && !mayPrescribe ? (
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: '4px 0 0' }} data-testid="pathway-enroll-plan-required">
              {t('pathways.enroll.planRequired')} — <Link to="/portalSubscriptions">{t('pathways.enroll.billing')}</Link>
            </Typography.Paragraph>
          ) : null}
        </div>
        <div>
          <Typography.Text>{t('pathways.enroll.startAt')}</Typography.Text>
          <DateFnsDatePicker
            locale={pickerLocale}
            style={{ width: '100%' }}
            showTime={{ format: 'HH:mm', minuteStep: 5 }}
            format="yyyy-MM-dd HH:mm"
            value={startAt}
            onChange={(d: Date | null) => setStartAt(d)}
            data-testid="pathway-enroll-start"
          />
        </div>
        {detail ? (
          <div data-testid="pathway-enroll-preview">
            <Typography.Text strong>{t('pathways.enroll.preview')}</Typography.Text>
            <Table
              size="small"
              rowKey={(r) => r.title}
              pagination={false}
              dataSource={landings}
              columns={[
                { title: t('pathways.enroll.column.step'), render: (_, r) => r.title },
                {
                  title: t('pathways.enroll.column.release'),
                  render: (_, r) => <span data-testid={`pathway-enroll-landing-${r.title}`}>{landingText(r.landing)}</span>,
                },
              ]}
            />
          </div>
        ) : null}
      </Space>
    </Drawer>
  );
};
